/**
 * Lossless conversion between the format model (Para[]) and a ProseMirror doc.
 * Run styling maps to marks; empty paragraphs map to a single empty run.
 */
import { Fragment, Mark, Node as PMNode } from "prosemirror-model";
import { screenplaySchema as schema } from "./schema";
import type { Align, Para, TextRun } from "../format/types";

const { strong, em, underline, font, runExtra } = schema.marks;

/** Build the marks for one run. */
function marksForRun(run: TextRun): Mark[] {
  const marks: Mark[] = [];
  if (run.bold) marks.push(strong.create());
  if (run.italic) marks.push(em.create());
  if (run.underline) marks.push(underline.create());
  if (run.font !== undefined) marks.push(font.create({ family: run.font }));
  if (run.extra && Object.keys(run.extra).length > 0) {
    marks.push(runExtra.create({ json: JSON.stringify(run.extra) }));
  }
  return marks;
}

/** Convert one Para to a PM `para` node. */
function paraToNode(para: Para): PMNode {
  const inline: PMNode[] = [];
  for (const run of para.runs) {
    if (run.text.length === 0) continue; // empty run → no text node
    inline.push(schema.text(run.text, marksForRun(run)));
  }
  // If nothing renders, stash the original runs so empty-run attributes survive.
  const blankRuns = inline.length === 0 && para.runs.length > 0 ? para.runs : null;
  const attrs = {
    styleName: para.style,
    align: para.align ?? null,
    bookmark: para.bookmark ?? null,
    extra: para.extra ?? null,
    styleExtra: para.styleExtra ?? null,
    blankRuns,
  };
  return schema.nodes.para.create(attrs, Fragment.from(inline));
}

/** Convert the format model's paragraphs into a full PM doc node. */
export function parasToDoc(paras: Para[]): PMNode {
  const source = paras.length > 0 ? paras : [{ style: "Scene Heading", runs: [{ text: "" }] }];
  return schema.nodes.doc.create(null, source.map(paraToNode));
}

/** Reconstruct a TextRun from a PM text node's content + marks. */
function runFromTextNode(text: string, marks: readonly Mark[]): TextRun {
  const run: TextRun = { text };
  for (const mark of marks) {
    switch (mark.type.name) {
      case "strong":
        run.bold = true;
        break;
      case "em":
        run.italic = true;
        break;
      case "underline":
        run.underline = true;
        break;
      case "font":
        run.font = String(mark.attrs.family);
        break;
      case "runExtra":
        run.extra = JSON.parse(String(mark.attrs.json)) as Record<string, string>;
        break;
    }
  }
  return run;
}

/** Convert one PM `para` node back to a Para. */
function nodeToPara(node: PMNode): Para {
  const runs: TextRun[] = [];
  node.forEach((child) => {
    if (child.isText) runs.push(runFromTextNode(child.text ?? "", child.marks));
  });
  // An empty paragraph is one empty run (matches the format model), unless we
  // stashed the original blank runs (to preserve empty-<text> attribute residue).
  if (runs.length === 0) {
    const blank = node.attrs.blankRuns as TextRun[] | null;
    if (blank && blank.length > 0) runs.push(...blank);
    else runs.push({ text: "" });
  }

  const para: Para = { style: String(node.attrs.styleName), runs };
  if (node.attrs.align) para.align = node.attrs.align as Align;
  if (node.attrs.bookmark) para.bookmark = String(node.attrs.bookmark);
  if (node.attrs.extra) para.extra = node.attrs.extra as Record<string, string>;
  if (node.attrs.styleExtra) {
    para.styleExtra = node.attrs.styleExtra as Record<string, string>;
  }
  return para;
}

/** Convert a PM doc node back into the format model's paragraphs. */
export function docToParas(doc: PMNode): Para[] {
  const paras: Para[] = [];
  doc.forEach((node) => {
    if (node.type.name === "para") paras.push(nodeToPara(node));
  });
  return paras;
}
